import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Header } from '@/components/layout/header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowLeft,
  Loader2,
  Plus,
  X,
  Variable,
  Phone,
  Link,
  Reply,
  Image,
  AlertCircle,
} from 'lucide-react'
import { useCreateWATemplate } from '@/hooks/useWATemplates'
import type { WATemplateCategory } from '@/lib/api-client'

export const Route = createFileRoute('/whatsapp-templates_/new')({
  component: NewTemplatePage,
})

type ButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'

interface TemplateButton {
  type: ButtonType
  text: string
  url?: string
  phoneNumber?: string
}

const LANGUAGES = [
  { value: 'ms', label: 'Malay (ms)' },
  { value: 'en', label: 'English (en)' },
  { value: 'en_US', label: 'English US (en_US)' },
]

function NewTemplatePage() {
  const navigate = useNavigate()
  const createMutation = useCreateWATemplate()

  // Form state
  const [name, setName] = React.useState('')
  const [category, setCategory] = React.useState<WATemplateCategory>('MARKETING')
  const [language, setLanguage] = React.useState('ms')
  const [headerType, setHeaderType] = React.useState<'none' | 'text' | 'image'>('none')
  const [headerText, setHeaderText] = React.useState('')
  const [bodyContent, setBodyContent] = React.useState('')
  const [footerContent, setFooterContent] = React.useState('')
  const [buttons, setButtons] = React.useState<TemplateButton[]>([])
  const [exampleValues, setExampleValues] = React.useState<string[]>([])
  const [error, setError] = React.useState<string | null>(null)

  const bodyRef = React.useRef<HTMLTextAreaElement>(null)

  // Count variables in body
  const varMatches = bodyContent.match(/\{\{(\d+)\}\}/g)
  const varNumbers = varMatches
    ? [...new Set(varMatches.map((m) => parseInt(m.replace(/\D/g, ''), 10)))].sort((a, b) => a - b)
    : []

  // Keep example values in sync with variable count
  React.useEffect(() => {
    setExampleValues((prev) => {
      const needed = varNumbers.length
      if (prev.length === needed) return prev
      if (prev.length < needed) return [...prev, ...Array(needed - prev.length).fill('')]
      return prev.slice(0, needed)
    })
  }, [varNumbers.length])

  // Auto-format name (lowercase + underscores)
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
    setName(val)
  }

  // Insert variable at cursor
  const insertVariable = () => {
    const nextVar = varNumbers.length > 0 ? Math.max(...varNumbers) + 1 : 1
    const textarea = bodyRef.current
    if (!textarea) {
      setBodyContent((prev) => `${prev}{{${nextVar}}}`)
      return
    }

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = bodyContent
    const before = text.substring(0, start)
    const after = text.substring(end)
    const newText = `${before}{{${nextVar}}}${after}`
    setBodyContent(newText)

    requestAnimationFrame(() => {
      const pos = start + `{{${nextVar}}}`.length
      textarea.selectionStart = pos
      textarea.selectionEnd = pos
      textarea.focus()
    })
  }

  // Button management
  const addButton = (type: ButtonType) => {
    if (buttons.length >= 3) return
    setButtons([...buttons, { type, text: '' }])
  }

  const updateButton = (index: number, updates: Partial<TemplateButton>) => {
    setButtons((prev) => prev.map((btn, i) => (i === index ? { ...btn, ...updates } : btn)))
  }

  const removeButton = (index: number) => {
    setButtons((prev) => prev.filter((_, i) => i !== index))
  }

  // Handle submit
  const handleSubmit = () => {
    setError(null)

    if (!name) {
      setError('Template name is required')
      return
    }
    if (!bodyContent.trim()) {
      setError('Body content is required')
      return
    }

    for (let i = 0; i < varNumbers.length; i++) {
      if (varNumbers[i] !== i + 1) {
        setError(`Variables must be sequential. Found {{${varNumbers[i]}}} but expected {{${i + 1}}}`)
        return
      }
    }

    if (varNumbers.length > 0) {
      const hasEmpty = exampleValues.some((v) => !v.trim())
      if (hasEmpty) {
        setError('All example values are required for variables')
        return
      }
    }

    for (const btn of buttons) {
      if (!btn.text.trim()) {
        setError('All buttons must have text')
        return
      }
      if (btn.type === 'URL' && !btn.url?.trim()) {
        setError('URL buttons must have a URL')
        return
      }
      if (btn.type === 'PHONE_NUMBER' && !btn.phoneNumber?.trim()) {
        setError('Phone buttons must have a phone number')
        return
      }
    }

    createMutation.mutate(
      {
        name,
        category,
        language,
        headerType: headerType === 'none' ? undefined : headerType,
        headerText: headerType === 'text' ? headerText : undefined,
        bodyContent,
        footerContent: footerContent || undefined,
        buttons: buttons.length > 0 ? buttons : undefined,
        exampleValues: exampleValues.length > 0 ? exampleValues : undefined,
      },
      {
        onSuccess: () => {
          navigate({ to: '/whatsapp-templates' })
        },
        onError: (err) => {
          setError(err.message)
        },
      }
    )
  }

  // Render preview body with resolved examples
  const renderPreviewBody = () => {
    let text = bodyContent || 'Your message body here...'
    if (varNumbers.length > 0) {
      for (let i = 0; i < varNumbers.length; i++) {
        const example = exampleValues[i]
        const placeholder = `{{${varNumbers[i]}}}`
        text = text.replace(
          placeholder,
          example ? example : `[Variable ${varNumbers[i]}]`
        )
      }
    }
    return text
  }

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Create Template"
        subtitle="Submit a new template to Meta for approval"
      />

      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2 text-text-muted hover:text-text-dark"
          onClick={() => navigate({ to: '/whatsapp-templates' })}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Templates
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Form */}
          <div className="space-y-4">
            {/* Template Name */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Template Name</Label>
                <Input
                  value={name}
                  onChange={handleNameChange}
                  placeholder="promo_raya_2026"
                  className="h-9 text-sm font-mono"
                />
                <p className="text-[10px] text-text-muted">
                  Lowercase, underscores only. e.g. appointment_reminder
                </p>
              </div>

              {/* Category + Language */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Category</Label>
                  <Select value={category} onValueChange={(v) => setCategory(v as WATemplateCategory)}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MARKETING">Marketing</SelectItem>
                      <SelectItem value="UTILITY">Utility</SelectItem>
                      <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => (
                        <SelectItem key={l.value} value={l.value}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Header */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-1.5">
              <Label className="text-xs font-medium">Header</Label>
              <Select value={headerType} onValueChange={(v) => setHeaderType(v as 'none' | 'text' | 'image')}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                </SelectContent>
              </Select>
              {headerType === 'text' && (
                <Input
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value)}
                  placeholder="Header text..."
                  maxLength={60}
                  className="h-9 text-sm mt-1.5"
                />
              )}
              {headerType === 'image' && (
                <p className="text-[10px] text-text-muted mt-1">
                  Image will be attached when sending via campaign
                </p>
              )}
            </div>

            {/* Body */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Body</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] px-2 gap-1 text-info hover:text-info"
                  onClick={insertVariable}
                >
                  <Variable className="w-3 h-3" />
                  Insert Variable
                </Button>
              </div>
              <Textarea
                ref={bodyRef}
                value={bodyContent}
                onChange={(e) => setBodyContent(e.target.value)}
                placeholder="Hai {{1}}, kami ada promosi khas untuk anda!"
                maxLength={1024}
                rows={4}
                className="text-sm resize-none"
              />
              <p className="text-[10px] text-text-muted text-right">
                {bodyContent.length}/1024
              </p>

              {/* Example Values */}
              {varNumbers.length > 0 && (
                <div className="space-y-2 p-3 rounded-lg bg-muted/30 border border-border">
                  <Label className="text-xs font-medium text-warning flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Example Values (required by Meta)
                  </Label>
                  {varNumbers.map((num, idx) => (
                    <div key={num} className="flex items-center gap-2">
                      <span className="w-10 text-xs text-text-muted font-mono shrink-0">
                        {`{{${num}}}`}
                      </span>
                      <Input
                        value={exampleValues[idx] || ''}
                        onChange={(e) => {
                          const newVals = [...exampleValues]
                          newVals[idx] = e.target.value
                          setExampleValues(newVals)
                        }}
                        placeholder="e.g. Ahmad"
                        className="h-8 text-xs flex-1"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-1.5">
              <Label className="text-xs font-medium">Footer (optional)</Label>
              <Input
                value={footerContent}
                onChange={(e) => setFooterContent(e.target.value)}
                placeholder="Klinik Amisi"
                maxLength={60}
                className="h-9 text-sm"
              />
            </div>

            {/* Buttons */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-2">
              <Label className="text-xs font-medium">Buttons (optional, max 3)</Label>
              {buttons.map((btn, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2.5 rounded-lg border border-border bg-muted/20">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-text-muted uppercase tracking-wider shrink-0 w-20">
                        {btn.type === 'QUICK_REPLY' && 'Quick Reply'}
                        {btn.type === 'URL' && 'URL'}
                        {btn.type === 'PHONE_NUMBER' && 'Phone'}
                      </span>
                      <Input
                        value={btn.text}
                        onChange={(e) => updateButton(idx, { text: e.target.value.slice(0, 25) })}
                        placeholder="Button text"
                        className="h-7 text-xs flex-1"
                        maxLength={25}
                      />
                    </div>
                    {btn.type === 'URL' && (
                      <Input
                        value={btn.url || ''}
                        onChange={(e) => updateButton(idx, { url: e.target.value })}
                        placeholder="https://example.com"
                        className="h-7 text-xs"
                      />
                    )}
                    {btn.type === 'PHONE_NUMBER' && (
                      <Input
                        value={btn.phoneNumber || ''}
                        onChange={(e) => updateButton(idx, { phoneNumber: e.target.value })}
                        placeholder="+60173142936"
                        className="h-7 text-xs"
                      />
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-text-muted hover:text-destructive shrink-0"
                    onClick={() => removeButton(idx)}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {buttons.length < 3 && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] gap-1"
                    onClick={() => addButton('QUICK_REPLY')}
                  >
                    <Reply className="w-3 h-3" />
                    Quick Reply
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] gap-1"
                    onClick={() => addButton('URL')}
                  >
                    <Link className="w-3 h-3" />
                    URL
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] gap-1"
                    onClick={() => addButton('PHONE_NUMBER')}
                  >
                    <Phone className="w-3 h-3" />
                    Phone
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Right: Live Preview */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-text-muted">Preview</Label>
            <div className="rounded-xl border border-border bg-[#0b141a] p-4 min-h-[400px] lg:sticky lg:top-6">
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-lg bg-[#005c4b] text-white p-3 shadow-sm space-y-1.5">
                  {headerType === 'text' && headerText && (
                    <p className="font-semibold text-sm">{headerText}</p>
                  )}
                  {headerType === 'image' && (
                    <div className="flex items-center justify-center w-full h-32 rounded bg-white/10 mb-2">
                      <Image className="w-8 h-8 text-white/40" />
                    </div>
                  )}

                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {renderPreviewBody()}
                  </p>

                  {footerContent && (
                    <p className="text-[11px] text-white/50 pt-1">{footerContent}</p>
                  )}

                  <div className="flex justify-end">
                    <span className="text-[10px] text-white/40">
                      {new Date().toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>

              {buttons.length > 0 && (
                <div className="flex justify-end mt-1">
                  <div className="max-w-[85%] w-full space-y-1">
                    {buttons.map((btn, idx) => (
                      <div
                        key={idx}
                        className="w-full p-2 text-center text-xs font-medium text-[#00a884] bg-[#1f2c34] rounded-lg border border-[#2a3942]"
                      >
                        {btn.type === 'URL' && <Link className='w-3 h-3 inline mr-1.5' />}
                        {btn.type === 'PHONE_NUMBER' && <Phone className='w-3 h-3 inline mr-1.5' />}
                        {btn.text || `Button ${idx + 1}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 text-[10px] text-text-muted">
              <span className="font-mono">{name || "template_name"}</span>
              <span>{category}</span>
              <span>{language.toUpperCase()}</span>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="max-w-2xl mx-auto mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-xs text-destructive flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {error}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="max-w-full mt-6 flex items-center justify-end gap-2 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/whatsapp-templates' })}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={createMutation.isPending || !name || !bodyContent.trim()}
          >
            {createMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Submit to Meta
          </Button>
        </div>
      </div>
    </div>
  )
}
